import { LEGAL_COUNTRIES } from "./legal";

/**
 * Workshop settings. Every setting needs `setting.<key>.label` and
 * `setting.<key>.description` in every locale file; the manual lists them all
 * with their defaults (tools/manual checks this).
 */
export type SettingType = "money" | "percent" | "currency" | "text" | "longtext" | "switch" | "select" | "days" | "count" | "hours" | "template" | "address";

export interface SettingDefinition {
  key: string;
  type: SettingType;
  /** Default value; null means the setup wizard must ask for it. */
  default: number | string | boolean | null;
  /** Choices of a "select" setting; each needs `setting.<key>.option.<value>`. */
  options?: readonly string[];
}

export const SETTINGS: readonly SettingDefinition[] = [
  // Who runs the workshop and where (first in Settings, right under the workshop's name)
  // Two addresses (owner, 2026-11-04): the registered seat (invoices, legal texts, the printed sheet's header) and
  // the workshop address where the car is (route links, "where the car is received"). `workshopAddress` was the
  // only address before; 20261218000000_ready_notice.sql copied it into `seatAddress`, so nothing was lost.
  // The effective values come from seatAddressOf / siteAddressOf in readyNotice.ts.
  // Who runs the workshop (owner, 2026-11-04): the workshop's name is the trade name ("Sanyi szervize"); the official
  // name of the company or sole trader is printed small under it with the seat and the numbers (address.ts
  // operatorLine). Filled from the contract (accept_terms) when empty.
  { key: "legalName", type: "text", default: null },
  // An "address" is stored in parts (address.ts: <prefix>Country (default HU), <prefix>PostalCode, <prefix>City,
  // <prefix>Street; prefixes "seat" and "workshop") and as the old one line value under the key itself (older readers).
  { key: "seatAddress", type: "address", default: null },
  { key: "workshopTaxNumber", type: "text", default: null },
  { key: "workshopRegNumber", type: "text", default: null },
  { key: "workshopAddressSameAsSeat", type: "switch", default: true },
  { key: "workshopAddress", type: "address", default: null },
  { key: "workshopPhone", type: "text", default: null },
  // Customers' replies to the program's e-mails go here
  { key: "workshopEmail", type: "text", default: null },
  { key: "hourlyRateNet", type: "money", default: null },
  { key: "defaultVatRate", type: "percent", default: 27 },
  { key: "currency", type: "currency", default: "HUF" },
  { key: "workOrderPrefix", type: "text", default: "ML" },
  // Default margin for new articles (stock keeping itself is the "stock" feature)
  { key: "defaultMarkupPercent", type: "percent", default: null },
  { key: "printNote", type: "longtext", default: null },
  // Legal texts of the official work order follow this country's rules
  { key: "legalCountry", type: "select", default: "HU", options: LEGAL_COUNTRIES },
  // Own warranty terms instead of the statutory text (e.g. a longer warranty)
  { key: "warrantyText", type: "longtext", default: null },
  // Who sends the SMS (the "sms" feature): Garagevo for a fee per SMS, or the workshop's own Android phone for free
  { key: "smsProvider", type: "select", default: "garagevo", options: ["garagevo", "ownPhone"] },
  // Reminders (the "reminders" feature): channels and how many days before the due date
  // (keep the defaults equal to REMINDER_DEFAULT_DAYS and public.due_reminders())
  { key: "reminderEmail", type: "switch", default: true },
  { key: "reminderSms", type: "switch", default: false },
  { key: "reminderInspectionDays", type: "days", default: 30 },
  { key: "reminderServiceDays", type: "days", default: 14 },
  // Two-step sign in for managers too (owner only; always required for the owner, except in test workshops)
  { key: "mfaManagers", type: "switch", default: false },
  // Intake with photos (the "intakePhotos" feature, its own panel; keep the defaults equal to intakeSettings in intake.ts)
  { key: "intakePhotoMode", type: "select", default: "offer", options: ["offer", "required"] },
  { key: "intakePhotoMin", type: "count", default: 5 },
  { key: "intakePhotoSend", type: "select", default: "ifEmail", options: ["ifEmail", "never"] },
  { key: "intakePhotoRetention", type: "select", default: "warrantyPlus1y", options: ["warrantyPlus1y", "5y"] },
  // At a computer the intake receipt is printed right after saving a new work order (the customer signs it on paper;
  // never on a phone, never when the intake is handed to the mechanic). Owner, 2026-11-04.
  { key: "intakePrintAfterSave", type: "switch", default: true },
  // Optional: a photo or scan of the signed paper receipt in the program ("Aláírt papír fotója" button on the work
  // order and a QR code on the printed receipt that opens it on a phone). Off by default (owner, 2026-11-04).
  { key: "intakePaperPhoto", type: "switch", default: false },
  // "Elkészült az autója" notice (its own panel, readyNotice.ts): the amount (rounded, "approx."), the opening hours
  // (given or not, and whether the notice shows them) and the two texts (null: the program's text in the language)
  { key: "readyNoticeAmount", type: "switch", default: true },
  { key: "openingHoursOn", type: "switch", default: false },
  { key: "openingHours", type: "hours", default: null },
  { key: "readyNoticeHours", type: "switch", default: true },
  { key: "readyNoticeEmail", type: "template", default: null },
  { key: "readyNoticeSms", type: "template", default: null },
];
